import React, { useState } from "react";
import DataList from "components/datatable/DataList";
import useAxios from "hooks/useAxios";
import useAxiosFunction from "hooks/useAxiosFunction";
import useMsgBox from "hooks/useMsgBox";
import { MyConst } from "data/MyConst";
import { RuleRequired } from "utils/MyUtils";
import { boolText, showEmpty } from "features/kms/system/SystemPageUtils";
import { formatKegiatanDate } from "./KegiatanPageUtils";

export default function KejuaraanManagementPage() {
  const [, , , axiosFetch] = useAxiosFunction();
  const [showMsg, confirmMsg] = useMsgBox();
  const [reloadKey, setReloadKey] = useState(0);
  const [tingkatResp] = useAxios({ method: "GET", url: "/references/tingkat-pertandingan", auth: true });
  const tingkatOptions = Array.isArray(tingkatResp?.data) ? tingkatResp.data : [];
  const canManage = true;
  const statusAktifOptions = [
    { id: "true", nama: "Aktif" },
    { id: "false", nama: "Nonaktif" },
  ];
  const liveScoreUrl = (idKejuaraan) => {
    const base = String(MyConst.BASEURL || window.location.origin || "").replace(/\/$/, "");
    return `${base}/live_score/${encodeURIComponent(idKejuaraan || "")}`;
  };

  const coltype = {
    id_kejuaraan: { type: "hidden", name: "id_kejuaraan" },
    nama_kejuaraan: {
      label: "Nama Kejuaraan",
      type: "text",
      name: "nama_kejuaraan",
      colsize: 12,
      validation: RuleRequired,
      width: 260,
      renderFunc: (val, col, item) => (
        <a
          href={liveScoreUrl(item?.id_kejuaraan)}
          target="_blank"
          rel="noopener noreferrer"
          title="Buka klasemen publik"
        >
          {showEmpty(val)}
        </a>
      ),
      renderExportFunc: (val) => showEmpty(val),
    },
    id_tingkat_pertandingan: {
      label: "Tingkat Pertandingan",
      type: "dropdown",
      name: "id_tingkat_pertandingan",
      colsize: 6,
      validation: RuleRequired,
      options: tingkatOptions,
      valueId: "id_tingkat_pertandingan",
      labelId: "nama",
      placeholder: "-Pilih Tingkat Pertandingan-",
      width: 170,
    },
    tingkat_pertandingan_name: {
      label: "Tingkat Pertandingan",
      renderFunc: (val) => showEmpty(val),
      width: 170,
    },
    tempat_kejuaraan: {
      label: "Tempat Kejuaraan",
      type: "text",
      name: "tempat_kejuaraan",
      colsize: 12,
      validation: RuleRequired,
      width: 220,
    },
    tanggal_awal: {
      label: "Tanggal Awal",
      type: "date",
      name: "tanggal_awal",
      colsize: 6,
      validation: RuleRequired,
      renderFunc: (val) => showEmpty(formatKegiatanDate(val)),
      renderExportFunc: (val) => showEmpty(formatKegiatanDate(val)),
      width: 130,
      align: "center",
    },
    tanggal_akhir: {
      label: "Tanggal Akhir",
      type: "date",
      name: "tanggal_akhir",
      colsize: 6,
      validation: RuleRequired,
      renderFunc: (val) => showEmpty(formatKegiatanDate(val)),
      renderExportFunc: (val) => showEmpty(formatKegiatanDate(val)),
      width: 130,
      align: "center",
    },
    aktif: {
      label: "Status",
      type: "checkbox",
      name: "aktif",
      renderFunc: (val) => boolText(val),
      renderExportFunc: (val) => boolText(val),
      colsize: 3,
      width: 90,
      align: "center",
    },
    action: { label: "Aksi", align: "center", width: 125 },
  };

  const columnsForm = [
    "nama_kejuaraan",
    "id_tingkat_pertandingan",
    "tempat_kejuaraan",
    "tanggal_awal",
    "tanggal_akhir",
  ];

  const saveHandle = async (form, defaultValue = {}) => {
    const id = form?.id_kejuaraan || defaultValue?.id_kejuaraan || "";
    return axiosFetch({
      method: id ? "PUT" : "POST",
      url: id ? `/kegiatan/kejuaraan/${id}` : "/kegiatan/kejuaraan",
      auth: true,
      data: {
        nama_kejuaraan: form?.nama_kejuaraan || "",
        id_tingkat_pertandingan: Number(form?.id_tingkat_pertandingan || 0),
        tempat_kejuaraan: form?.tempat_kejuaraan || "",
        tanggal_awal: form?.tanggal_awal || "",
        tanggal_akhir: form?.tanggal_akhir || "",
      },
    });
  };

  const importPrestasiHandle = async (item) => {
    const ok = await confirmMsg(
      "Konfirmasi",
      `Apakah Anda yakin akan mengimport prestasi dari kejuaraan ${item?.nama_kejuaraan || "ini"}?`,
    );
    if (!ok) return;

    const result = await axiosFetch({
      method: "POST",
      url: `/kegiatan/kejuaraan/${item?.id_kejuaraan}/import-prestasi`,
      auth: true,
    });
    if (result?.data?.success === true) {
      const total = Number(result?.data?.total || 0);
      const inserted = Number(result?.data?.inserted || 0);
      const updated = Number(result?.data?.updated || 0);
      await showMsg("Informasi", `Import Prestasi berhasil dilakukan. Total data: ${total}, baru: ${inserted}, diperbarui: ${updated}.`);
      setReloadKey((current) => current + 1);
    } else {
      await showMsg("Error", result?.message || "Import Prestasi gagal dilakukan.");
    }
  };

  const renderActionExtension = (val, col, item) => (
    <>
      <a
        href="#"
        onClick={(event) => {
          event.preventDefault();
          importPrestasiHandle(item);
        }}
        className="btn btn-success btn-xs"
        title="Import Prestasi"
      >
        <i className="fa fa-file-import"></i>
      </a>
      &nbsp;
    </>
  );

  return (
    <DataList
      title="Kejuaraan"
      coltype={coltype}
      reload={reloadKey}
      dlist={{
        url: "/kegiatan/kejuaraan",
        columns: [
          "no",
          "nama_kejuaraan",
          "tingkat_pertandingan_name",
          "tempat_kejuaraan",
          "tanggal_awal",
          "tanggal_akhir",
          "aktif",
          ...(canManage ? ["action"] : []),
        ],
        filter: [
          { dtype: "q", name: "q", colsize: 3 },
          {
            dtype: "dropdown",
            name: "id_tingkat_pertandingan",
            label: "Tingkat Pertandingan",
            colsize: 3,
            options: tingkatOptions,
            valueId: "id_tingkat_pertandingan",
            labelId: "nama",
            placeholder: "-Semua Tingkat-",
          },
          {
            dtype: "dropdown",
            name: "aktif",
            label: "Status",
            colsize: 3,
            options: statusAktifOptions,
            valueId: "id",
            labelId: "nama",
            placeholder: "-Semua Status-",
          },
        ],
        sort_by: ["nama_kejuaraan", "tingkat_pertandingan_name", "tempat_kejuaraan", "tanggal_awal", "tanggal_akhir"],
        render_action_extension: canManage ? renderActionExtension : null,
      }}
      addconfig={{
        columns: columnsForm,
        title: "Tambah Kejuaraan",
        label_tool: "Tambah Kejuaraan",
        saveHandle,
      }}
      editconfig={{
        columns: ["id_kejuaraan", ...columnsForm],
        title: "Ubah Kejuaraan",
        url: "/kegiatan/kejuaraan",
        urlitem: "/kegiatan/kejuaraan",
        cellid: "id_kejuaraan",
        saveHandle,
      }}
      delconfig={{
        url: "/kegiatan/kejuaraan",
        cellid: "id_kejuaraan",
        cellname: "nama_kejuaraan",
      }}
      activateconfig={{
        url: "/kegiatan/kejuaraan",
        cellid: "id_kejuaraan",
        cellname: "nama_kejuaraan",
        cellactivate: "aktif",
        labelactivate: "Aktifkan",
        labelinactivate: "Non-aktifkan",
        cfmactivate: "mengaktifkan",
        cfminactivate: "menonaktifkan",
        infoactivate: "diaktifkan",
        infoinactivate: "dinonaktifkan",
      }}
      pdflist={{ title: "Kejuaraan" }}
      xlslist={{ title: "Kejuaraan" }}
    />
  );
}
